import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ArtworkReviewModel, ArtworkReviewRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['artworkReviews', 'statuses']

type ArtworkReviewFilters = {
  artwork_id?: number,
  status_id?: number,
  status_key?: string,
  user_id?: number,
}

type UseArtworkReviewsOptions = {
  filters?: ArtworkReviewFilters,
  performHttpRequests?: boolean,
  requestOptions?: ArtworkReviewRequestOptions,
}

function useArtworkReviews(options: UseArtworkReviewsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    artwork_id: filterArtworkId,
    status_id: filterStatusId,
    status_key: filterStatusKey,
    user_id: filterUserId,
  } = filters

  const {
    updatedEntities: {
      artworkReviews: artworkReviewsUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { artworkReviews, statuses } = useSelector(reduxState => reduxState.entities)

  const filteredArtworkReviews = useMemo(() => {
    const filtered = Object.values(artworkReviews).filter((artworkReview: ArtworkReviewModel) => {
      const { artwork_id, status_id, user_id } = artworkReview

      // @ts-expect-error TS2538
      const status = statuses[status_id]

      const matchArtworkId = matchFilterNumber(artwork_id, filterArtworkId)
      const matchStatusId = matchFilterNumber(status_id, filterStatusId)
      const matchStatusKey = typeof filterStatusKey !== 'undefined' ? status.key === filterStatusKey : true
      const matchUserId = matchFilterNumber(user_id, filterUserId)

      return matchArtworkId && matchStatusId && matchStatusKey && matchUserId
    })

    return sortArrayBy(filtered, 'asc')
  }, [artworkReviewsUpdatedAt, statusesUpdatedAt, JSON.stringify(filters)])

  const filteredArtworkReviewsCount = filteredArtworkReviews.length
  const hasFilteredArtworkReviews = !!filteredArtworkReviewsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredArtworkReviewsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingArtworkReviews } = useReduxAction(
    'artworkReviews',
    'loadArtworkReviews',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredArtworkReviews,
    filteredArtworkReviewsCount,
    hasFilteredArtworkReviews,
    loading: loadingArtworkReviews,
  }
}

export default useArtworkReviews
